import type { CarAttributes } from 'src/types/car';

import { FUEL_LABELS, GEARBOX_LABELS, BODY_TYPE_LABELS } from 'src/types/car';

/**
 * The body, gearbox and fuel of a car in the words a buyer reads (decision №38): three labelled
 * rows on a listing, and the gearbox and the fuel after the mileage on a sale card.
 *
 * A value this build has no word for — an api that has learnt a new one — is shown as it came:
 * a raw `wagon` tells the buyer more than a blank, and a lookup that missed must never print
 * "undefined". A value the dealer left empty is not a row at all.
 */
const wordFor = (labels: Record<string, string>, value: string | null | undefined): string | undefined => {
  if (!value) return undefined;

  // Only a word of the list itself: `constructor` is a key every object answers to, and not a fuel.
  return Object.prototype.hasOwnProperty.call(labels, value) ? labels[value] : value;
};

export const bodyTypeLabel = (value: string | null | undefined) => wordFor(BODY_TYPE_LABELS, value);
export const gearboxLabel = (value: string | null | undefined) => wordFor(GEARBOX_LABELS, value);
export const fuelLabel = (value: string | null | undefined) => wordFor(FUEL_LABELS, value);

export interface CarAttributeRow {
  label: 'Body' | 'Gearbox' | 'Fuel';
  value: string;
}

/** The rows of a listing screen, in a fixed order — only those the dealer filled in. */
export const carAttributeRows = (car: CarAttributes): CarAttributeRow[] => {
  const rows: [CarAttributeRow['label'], string | undefined][] = [
    ['Body', bodyTypeLabel(car.bodyType)],
    ['Gearbox', gearboxLabel(car.gearbox)],
    ['Fuel', fuelLabel(car.fuel)],
  ];

  return rows.flatMap(([label, value]) => (value ? [{ label, value }] : []));
};
